/* eslint-disable import/max-dependencies */
import { useDispatch, useSelector } from "react-redux";
import { useHandleRequest } from "@fas/cpa-state-manager/redux/hooks";
import { useFormField, addNotification } from "@fas/ui-framework";
import { getLoadingState } from "@fas/cpa-state-manager/services/selectors/loading";
import { getUserInfo as getUserInfoSelector } from "@fas/cpa-state-manager/services/selectors/userInfo";
import { USER_INFO } from "@fas/cpa-state-manager/redux/constants";
import { updateInfoBanner } from "../../services/deprecatedApi";
import { INFO_BANNER } from "../../constants";
import type { DefaultUserInfoState } from "../useUserInfo/useUserInfo";

export type UseInfoBannerModalHook = {
  isLoading: boolean;
  open: boolean;
  email: string;
  affiliateId: string;
  onChangeEmail: (arg0: string) => unknown;
  onSendData: (params: { isBannerAccepted: boolean }) => Promise<void>;
  onShowModal: () => void;
  onCloseModal: () => void;
};

function useValue<T>(key: string): {
  value: T;
  onChange: (arg0: T) => any;
} {
  const { value, onChange } = useFormField<T>(INFO_BANNER, key);
  return {
    value,
    onChange,
  };
}

export const useInfoBannerModal: () => UseInfoBannerModalHook = () => {
  const dispatch = useDispatch();
  const { email: affiliateEmail, id: affiliateId } = useSelector(
    getUserInfoSelector
  ) as DefaultUserInfoState;
  const isLoadingUserInfo = useSelector((state) =>
    getLoadingState(state, USER_INFO)
  );
  const isFetchedUserInfo = useSelector((state) =>
    getLoadingState(state, `${USER_INFO}-fetched`)
  );
  const { value: open, onChange: setModalOpen } =
    useValue<boolean>("openModal");
  const { value: email = affiliateEmail, onChange: onChangeEmail } =
    useValue<string>("email");
  const { onChange: setShowBanner } = useValue<boolean>("open");
  const { isLoading, changeLoading, onFail, onFinally } = useHandleRequest(
    `${INFO_BANNER}-update`
  );

  const onShowModal = () => {
    setModalOpen(true);
  };

  const onSendData = async ({
    isBannerAccepted = false,
  }: {
    isBannerAccepted: boolean;
  }) => {
    changeLoading(true);
    updateInfoBanner(email, isBannerAccepted)
      .then(() => {
        setModalOpen(false);
        setShowBanner(false);

        if (isBannerAccepted) {
          dispatch(
            addNotification({
              severity: "success",
              message: "Thank you! Your request has been successfully sent.",
            })
          );
        }
      })
      .catch(onFail)
      .finally(onFinally);
  };

  const onCloseModal = () => {
    setModalOpen(false);
  };

  return {
    isLoading: isLoading || isLoadingUserInfo || !isFetchedUserInfo,
    open: Boolean(open),
    email,
    affiliateId,
    onChangeEmail,
    onSendData,
    onShowModal,
    onCloseModal,
  };
};
